import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { formatNumber } from "../../../../helper/numberFormatter";
import { formatDate } from "../../../../helper/dateFormatter";

const GenericTable = ({ data }) => {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const isDateString = (value) => {
    if (typeof value !== "string") return false;
    // Check for ISO date format (YYYY-MM-DDTHH:mm:ss)
    const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
    return dateRegex.test(value);
  };

  //format table cell value
  const formatCellValue = (value, key) => {
    if (isDateString(value)) {
      return formatDate(value);
    }
    if (typeof value === "string" && value.endsWith("%")) {
      let num = parseFloat(value.replace("%", ""));
      // if (isNaN(num)) return value; // not a number, return as is
      // return Math.round(num) + "%";
      if (!Number.isFinite(num)) return value; // not a number, return as is
      return `${formatNumber(num, 2)}%`; // apply precision
    } else if (typeof value === "number") {
      return formatNumber(value); // apply precision and delimiter
    }
    return value;
  };

  const sortedData = React.useMemo(() => {
    // Ensure data is always an array
    const safeData = data || [];
    if (!sortConfig.key) return safeData;
    const sorted = [...safeData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      // Handle date strings by converting to Date objects for comparison
      if (isDateString(aValue) || isDateString(bValue)) {
        // Convert to Date objects, invalid dates become 0
        const aDate = isDateString(aValue) ? new Date(aValue) : new Date(0);
        const bDate = isDateString(bValue) ? new Date(bValue) : new Date(0);

        if (aDate < bDate) return sortConfig.direction === "asc" ? -1 : 1;
        if (aDate > bDate) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      }

      // Handle regular string/number comparison
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data, sortConfig]);

  // Get paginated data
  const paginatedData = React.useMemo(() => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, page, perPage]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPerPage(event.target.value);
    setPage(1); // Reset to first page when changing rows per page
  };

  //convert camelCase to space separated words for table header
  const formatHeaderText = (key) => {
    // Add space before capital letters and capitalize first letter
    return key
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
      .trim(); // Remove any leading/trailing spaces
  };

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingTop: "4rem",
          paddingBottom: "4rem",
        }}
      >
        {t("NO_RESULTS_FOUND")}
      </div>
    );
  }

  // Get all unique keys from the data (in case some rows have different keys)
  const allKeys = Array.from(
    data.reduce((keys, row) => {
      Object.keys(row).forEach((key) => keys.add(key));
      return keys;
    }, new Set()),
  );

  return (
    <>
      <TableContainer>
        <Table className="newGlobalTable">
          <TableHead>
            <TableRow>
              {allKeys.map((key) => (
                <TableCell
                  key={key}
                  onClick={() => handleSort(key)}
                  style={{ cursor: "pointer" }}
                >
                  {formatHeaderText(key)}
                  <span
                    style={{
                      marginLeft: 4,
                      display: "inline-flex",
                      flexDirection: "column",
                      color: "#888",
                    }}
                  >
                    <KeyboardArrowUpIcon
                      style={{
                        fontSize: 16,
                        marginBottom: -6,
                        color:
                          sortConfig.key === key &&
                          sortConfig.direction === "asc"
                            ? "#222"
                            : "#888",
                      }}
                    />
                    <KeyboardArrowDownIcon
                      style={{
                        fontSize: 16,
                        marginTop: -2,
                        color:
                          sortConfig.key === key &&
                          sortConfig.direction === "desc"
                            ? "#222"
                            : "#888",
                      }}
                    />
                  </span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* {sortedData.map((row, idx) => (
              <TableRow key={idx}>
                {allKeys.map((key) => (
                  <TableCell key={key}>{formatCellValue(row[key], key)}</TableCell>
                ))}
              </TableRow>
            ))} */}
            {paginatedData.map((row, idx) => (
              <TableRow key={idx}>
                {allKeys.map((key) => (
                  <TableCell key={key}>
                    {formatCellValue(row[key], key)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {data.length > 0 && (
        <div
          className="global-custom-pagination"
          style={{ background: "rgba(196, 196, 196, 0.2)" }}
        >
          <label style={{ float: "left", padding: "5px" }}>
            {t("TOTAL_RECORDS")}: {formatNumber(data.length)}
          </label>
          <label>
            {/* Rows per page select */}
            {t("ROWS_PER_PAGE")}
            <Select value={perPage} onChange={handleChangeRowsPerPage}>
              <MenuItem value={5} classes={{ root: "treeRowPerPageItem" }}>
                5
              </MenuItem>
              <MenuItem value={10} classes={{ root: "treeRowPerPageItem" }}>
                10
              </MenuItem>
              <MenuItem value={50} classes={{ root: "treeRowPerPageItem" }}>
                50
              </MenuItem>
              <MenuItem value={100} classes={{ root: "treeRowPerPageItem" }}>
                100
              </MenuItem>
            </Select>
          </label>
          {/* Pagination component */}
          <Pagination
            page={page}
            count={Math.ceil(data.length / perPage)}
            onChange={handleChangePage}
            showFirstButton
            showLastButton
          />
        </div>
      )}
    </>
  );
};

export default GenericTable;
